import { memo } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { CustomButton } from '../../../../../components';
import type { ScheduleConflict, ScheduleConflictKind } from '../../../../../features/schedule/scheduleConflictDetector';
import { parseMergedKinds, formatScheduleConflictMessage } from '../../../../../features/schedule/scheduleConflictDisplay';
import type { IMatchScheduleItem } from '../../../../../types/schedule';
import type { Class } from '../../../../../types/tournament';
import './ScheduleConflictDetailModal.scss';

export type ScheduleConflictDetailModalProps = {
  show: boolean;
  conflict: ScheduleConflict | null;
  matchById: Map<number, IMatchScheduleItem>;
  timeZone: string;
  classIdToObject: Record<string, Class>;
  onClose: () => void;
  onFocusOnGrid: (conflict: ScheduleConflict) => void;
  onViewMatch: (matchId: number) => void;
  formatMatchSlot: (match: IMatchScheduleItem | undefined, timeZone: string) => string;
};

const KIND_I18N: Record<ScheduleConflictKind, string> = {
  PLAYER_OVERLAP: 'scheduleConflictKindPlayerOverlap',
  TEAM_OVERLAP: 'scheduleConflictKindTeamOverlap',
  COURT_OVERLAP: 'scheduleConflictKindCourtOverlap',
  MISSING_END: 'scheduleConflictKindMissingEnd',
  BRACKET_ORDERING: 'scheduleConflictKindBracketOrdering',
  BOX_ROUND_ORDERING: 'scheduleConflictKindBoxRoundOrdering',
  PARENT_CLASS_ORDERING: 'scheduleConflictKindParentClassOrdering',
};

export const ScheduleConflictDetailModal = memo(function ScheduleConflictDetailModal({
  show,
  conflict,
  matchById,
  timeZone,
  classIdToObject,
  onClose,
  onFocusOnGrid,
  onViewMatch,
  formatMatchSlot,
}: ScheduleConflictDetailModalProps) {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';

  if (!conflict) return null;

  const [idA, idB] = conflict.matchIds;
  const matchA = matchById.get(idA);
  const matchB = matchById.get(idB);
  const classObject =
    classIdToObject[String(matchA?.classId ?? conflict.entityId)] ??
    classIdToObject[matchA?.classId ?? conflict.entityId];
  const classLabel = classObject?.name ?? conflict.entityLabel;

  const mergedKinds = parseMergedKinds(conflict.messageParams);
  const kindLabel =
    mergedKinds && mergedKinds.length > 1
      ? t(prefix + 'scheduleConflictOverlapCombined', {
          kinds: mergedKinds
            .map((k) => t(prefix + KIND_I18N[k]))
            .join(t(prefix + 'scheduleConflictOverlapCombinedJoiner')),
        })
      : t(prefix + KIND_I18N[conflict.kind]);

  const bodyText = formatScheduleConflictMessage(
    conflict,
    matchA,
    matchB,
    (match) => formatMatchSlot(match, timeZone),
    t
  );

  const severityLabel =
    conflict.severity === 'error'
      ? t(prefix + 'scheduleConflictDetailSeverityError')
      : t(prefix + 'scheduleConflictDetailSeverityWarning');

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      className="schedule-conflict-detail-modal"
      dialogClassName="schedule-conflict-detail-modal__dialog"
    >
      <Modal.Header closeButton>
        <Modal.Title>{t(prefix + 'scheduleConflictDetailTitle')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <dl className="schedule-conflict-detail-modal__meta row mb-3">
          <dt className="col-sm-4">{t(prefix + 'scheduleConflictDetailKind')}</dt>
          <dd className="col-sm-8">{kindLabel}</dd>
          <dt className="col-sm-4">{t(prefix + 'scheduleConflictDetailSeverity')}</dt>
          <dd className="col-sm-8">{severityLabel}</dd>
          <dt className="col-sm-4">{t(prefix + 'scheduleConflictDetailClass')}</dt>
          <dd className="col-sm-8">{classLabel}</dd>
        </dl>
        <p className="schedule-conflict-detail-modal__body mb-3">{bodyText}</p>
        <div className="schedule-conflict-detail-modal__matches d-flex flex-column gap-2">
          {matchA ? (
            <div className="schedule-conflict-detail-modal__match-row d-flex flex-wrap align-items-center justify-content-between gap-2">
              <span className="small">
                <span className="text-muted">{t(prefix + 'scheduleConflictDetailMatchA')}: </span>
                {formatMatchSlot(matchA, timeZone)}
              </span>
              <CustomButton
                btnText={t(prefix + 'scheduleConflictDetailViewMatch')}
                variant="outline-secondary"
                title={t(prefix + 'scheduleConflictDetailViewMatchHint')}
                cb={() => onViewMatch(matchA.id)}
              />
            </div>
          ) : null}
          {matchB ? (
            <div className="schedule-conflict-detail-modal__match-row d-flex flex-wrap align-items-center justify-content-between gap-2">
              <span className="small">
                <span className="text-muted">{t(prefix + 'scheduleConflictDetailMatchB')}: </span>
                {formatMatchSlot(matchB, timeZone)}
              </span>
              <CustomButton
                btnText={t(prefix + 'scheduleConflictDetailViewMatch')}
                variant="outline-secondary"
                title={t(prefix + 'scheduleConflictDetailViewMatchHint')}
                cb={() => onViewMatch(matchB.id)}
              />
            </div>
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <CustomButton
          btnText={t(prefix + 'scheduleConflictDetailClose')}
          variant="outline-secondary"
          cb={onClose}
        />
        <CustomButton
          btnText={t(prefix + 'scheduleConflictDetailFocusGrid')}
          variant="primary"
          title={t(prefix + 'scheduleConflictDetailFocusGridHint')}
          cb={() => onFocusOnGrid(conflict)}
        />
      </Modal.Footer>
    </Modal>
  );
});
